import React, { useContext, useState, useEffect } from "react";
import {
  Button,
  Spinner,
  Modal,
  Form,
  Table,
  Alert,
  Card,
} from "react-bootstrap";
import { isValidPermission } from "../../../../services/AuthService";
import { ServiceContext } from "../../../../context/ServiceContext";
import {
  useGetWorkDaysList,
  useCreateEmployeeWorkDays,
  useEmployeeWorkDaysUpdate,
  useGetEmployeeWorkDaysDetail,
} from "../../../../actions/timesheet-actions";

function EmployeeWorkDayDetails(props) {
  const { canEditEmployee } = useContext(ServiceContext);
  const employee_uuid = props?.employeeuuid;
  const [showModal, setShowModal] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [formData, setFormData] = useState({
    start_time: "",
    end_time: "",
    total_hours: "",
  });

  // Fetch data with error handling
  const { data: workDaysList = [], isLoading: isLoadingDays } =
    useGetWorkDaysList();

  console.log("workDaysList", workDaysList);

  const {
    data: employeeWorkDays = [],
    isLoading: isLoadingEmployeeDays,
    refetch,
  } = useGetEmployeeWorkDaysDetail(employee_uuid);

  console.log("employeeWorkDays", employeeWorkDays);

  // Mutation hooks
  const { mutate: createMutation, isLoading: postLoading } =
    useCreateEmployeeWorkDays();
  const { mutate: updateMutation, isLoading: updateLoading } =
    useEmployeeWorkDaysUpdate();

  const calculateTotalHours = (startTime, endTime) => {
    if (!startTime || !endTime) return "";

    try {
      const [startHours, startMinutes] = startTime.split(":").map(Number);
      const [endHours, endMinutes] = endTime.split(":").map(Number);

      const startTotal = startHours + startMinutes / 60;
      const endTotal = endHours + endMinutes / 60;

      let total = endTotal - startTotal;
      if (total < 0) total += 24;

      return total.toFixed(2);
    } catch (error) {
      console.error("Error calculating hours:", error);
      return "";
    }
  };

  const handleOpenModal = (day) => {
    if (!day?.id) return;

    setSelectedDay(day);
    const existingDay = Array.isArray(employeeWorkDays)
      ? employeeWorkDays.find((d) => d?.day === day.id)
      : null;

    setFormData({
      start_time: existingDay?.start_time || "",
      end_time: existingDay?.end_time || "",
      total_hours: existingDay?.total_hours || "",
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedDay(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      total_hours:
        name === "start_time" || name === "end_time"
          ? calculateTotalHours(
              name === "start_time" ? value : prev.start_time,
              name === "end_time" ? value : prev.end_time
            )
          : prev.total_hours,
    }));
  };

  const handleSubmit = async () => {
    if (!selectedDay?.id) return;

    const payload = {
      employee_uuid: props.employeeuuid,
      employee: props.employee_id,
      day: selectedDay.id,
      start_time: formData.start_time || null,
      end_time: formData.end_time || null,
      total_hours: formData.total_hours || null,
    };

    try {
      const existingDay = Array.isArray(employeeWorkDays)
        ? employeeWorkDays.find((d) => d?.day === selectedDay.id)
        : null;

      if (existingDay?.id) {
        await updateMutation({ id: existingDay.id, ...payload });
        await refetch();
      } else {
        await createMutation(payload);
        await refetch();
      }

      await refetch();
      handleCloseModal();
    } catch (error) {
      console.error("Error saving work day:", error);
    }
  };

  const handleReset = async () => {
    if (!selectedDay?.id) return;

    const existingDay = Array.isArray(employeeWorkDays)
      ? employeeWorkDays.find((d) => d?.day === selectedDay.id)
      : null;

    if (!existingDay?.id) return;

    const payload = {
      employee_uuid: props.employeeuuid,
      employee: props.employee_id,
      day: selectedDay.id,
      start_time: null,
      end_time: null,
      total_hours: null,
    };

    try {
      await updateMutation({
        id: existingDay.id,
        ...payload,
      });

      setFormData({ start_time: "", end_time: "", total_hours: "" });
      await refetch();
      handleCloseModal();
    } catch (error) {
      console.error("Error resetting schedule:", error);
    }
  };

  // Safe data processing
  const sortedWorkDays = Array.isArray(workDaysList)
    ? [...workDaysList].sort((a, b) => (a?.sort || 0) - (b?.sort || 0))
    : [];

  const employeeDaysMap = Array.isArray(employeeWorkDays)
    ? employeeWorkDays.reduce((map, day) => {
        if (day?.day) map[day.day] = day;
        return map;
      }, {})
    : {};

  const formatDisplayValue = (value) => value || "Not set";

  return (
    <Card className="mb-4" id="workdays">
      <Card.Header className="d-flex justify-content-between">
        <Card.Title>
          <i className="fa fa-calendar-alt me-2 text-primary"></i>
          Workdays Schedule
        </Card.Title>
      </Card.Header>

      <Card.Body>
        {isLoadingDays || isLoadingEmployeeDays ? (
          <div className="text-center">
            <Spinner animation="border" />
          </div>
        ) : (
          <div className="table-responsive">
            <Table bordered hover>
              <thead>
                <tr>
                  <th>Field</th>
                  {sortedWorkDays?.map((day) => (
                    <th key={day.id}>{day.day}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="fw-bold">Start Time</td>
                  {sortedWorkDays?.map((day) => (
                    <td key={`start-${day.id}`}>
                      {formatDisplayValue(employeeDaysMap[day.id]?.start_time)}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="fw-bold">End Time</td>
                  {sortedWorkDays?.map((day) => (
                    <td key={`end-${day.id}`}>
                      {formatDisplayValue(employeeDaysMap[day.id]?.end_time)}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="fw-bold">Total Hours</td>
                  {sortedWorkDays.map((day) => (
                    <td key={`hours-${day.id}`}>
                      {formatDisplayValue(employeeDaysMap[day.id]?.total_hours)}
                    </td>
                  ))}
                </tr>
                {canEditEmployee &&
                  isValidPermission(
                    "employee.add_employeepassport",
                    props.getPermissionList
                  ) && (
                    <tr>
                      <td className="fw-bold">Action</td>
                      {sortedWorkDays?.map((day) => {
                        const hasData = !!employeeDaysMap[day.id];
                        return (
                          <td key={`action-${day.id}`}>
                            <Button
                              variant={hasData ? "outline-primary" : "primary"}
                              size="sm"
                              onClick={() => handleOpenModal(day)}
                            >
                              {hasData ? "Edit" : "Add"}
                            </Button>
                          </td>
                        );
                      })}
                    </tr>
                  )}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedDay ? `${selectedDay.day} Schedule` : "Add Work Day"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Start Time</Form.Label>
              <Form.Control
                type="time"
                name="start_time"
                value={formData.start_time}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>End Time</Form.Label>
              <Form.Control
                type="time"
                name="end_time"
                value={formData.end_time}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Total Hours</Form.Label>
              <Form.Control
                type="text"
                name="total_hours"
                value={formData.total_hours}
                readOnly
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <div>
            {selectedDay && employeeDaysMap[selectedDay.id] && (
              <Button
                variant="outline-danger"
                onClick={handleReset}
                disabled={updateLoading}
                className="me-2"
              >
                Reset
                {/* {updateLoading ? (
                  <>
                    <Spinner size="sm" animation="border" /> Resetting...
                  </>
                ) : (
                  "Reset"
                )} */}
              </Button>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={handleCloseModal}>
              Close
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={postLoading || updateLoading}
              className="ms-2"
            >
              {createMutation.isLoading || updateMutation.isLoading ? (
                <>
                  <Spinner size="sm" animation="border" /> Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </Card>
  );
}

export default EmployeeWorkDayDetails;
